<template>
    <v-form @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="
                    $t('users-form.texts.title', {
                        'title-prefix': titlePrefix,
                    })
                "
                :showBackButton="showBackButton"
            >
            </card-header>
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.name"
                            :label="$t('users-form.labels.name')"
                            :counter="80"
                            :error-messages="nameErrors"
                            @input="$v.model.name.$touch()"
                            @blur="$v.model.name.$touch()"
                            autofocus
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.email"
                            :label="$t('users-form.labels.email')"
                            :counter="70"
                            :error-messages="emailErrors"
                            :disabled="isEdit"
                            @input="$v.model.email.$touch()"
                            @blur="$v.model.email.$touch()"
                        ></v-text-field>
                        <v-btn v-if="isEdit" @click="changeEmail">
                            {{ $t('users-form.labels.change-email') }}
                        </v-btn>
                        <v-dialog v-model="showChangeEmailModal" width="350px">
                            <v-card>
                                <v-col>
                                    <p>
                                        {{
                                            $t(
                                                'users-form.messages.change-email-message',
                                            )
                                        }}
                                    </p>
                                    <v-text-field
                                        flat
                                        v-model="model.newEmail"
                                        :label="$t('users-form.labels.email')"
                                        :counter="70"
                                        :error-messages="changeEmailError"
                                        @input="$v.model.newEmail.$touch()"
                                        @blur="$v.model.newEmail.$touch()"
                                    ></v-text-field>
                                    <v-btn @click="sendConfirmationEmail">
                                        {{ $t('users-form.buttons.save') }}
                                    </v-btn>
                                </v-col>
                            </v-card>
                        </v-dialog>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.password"
                            type="password"
                            :label="$t('users-form.labels.password')"
                            :error-messages="passwordErrors"
                            @input="$v.model.password.$touch()"
                            @blur="$v.model.password.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            flat
                            v-model="model.password_confirmation"
                            type="password"
                            :label="
                                $t('users-form.labels.password-confirmation')
                            "
                            :error-messages="passwordConfirmationErrors"
                            @input="$v.model.password_confirmation.$touch()"
                            @blur="$v.model.password_confirmation.$touch()"
                        ></v-text-field>
                    </v-col>
                    <template v-if="!isDialog">
                        <v-col cols="12" xs="12" sm="12" md="6">
                            <v-select
                                v-model="model.roles"
                                :items="roles"
                                label="Perfil de usuários"
                                multiple
                                chips
                                deletable-chips
                                clearable
                                item-text="name"
                                return-object
                                :disabled="model.id == 1"
                            ></v-select>
                        </v-col>
                    </template>
                </v-row>
            </v-card-text>

            <form-actions
                :loadingSave="loading"
                :showBackButton="showBackButton"
            ></form-actions>
        </v-card>
    </v-form>
</template>

<script>
import BaseModel from '@/shared/orm/Model'
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import { validationMixin } from 'vuelidate'
import {
    required,
    minLength,
    maxLength,
    email,
    sameAs,
} from 'vuelidate/lib/validators'
import Role from '@/models/Role'
import axios from '@/plugins/axios'

export default {
    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        titlePrefix: String,
        isEdit: {
            type: Boolean,
            default: () => {
                return false
            },
        },
        showBackButton: {
            type: Boolean,
            default: () => {
                return true
            },
        },
        isDialog: {
            type: Boolean,
            default: () => {
                return false
            },
        },
    },

    data: () => {
        return {
            loading: false,
            roles: null,
            showChangeEmailModal: false,
            changeEmailError: '',
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    mixins: [validationMixin],

    validations() {
        if (!this.isEdit) {
            return {
                model: {
                    name: { required, maxLength: maxLength(80) },
                    email: { required, email },
                    newEmail: {},
                    password: { required, minLength: minLength(6) },
                    password_confirmation: { sameAs: sameAs('password') },
                },
            }
        } else {
            return {
                model: {
                    name: { required, maxLength: maxLength(80) },
                    email: { required, email },
                    newEmail: {},
                    password: { minLength: minLength(6) },
                    password_confirmation: { sameAs: sameAs('password') },
                },
            }
        }
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('users-form.bus.save-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$store.dispatch('auth/syncUser')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('users-form.bus.save-success', { name: clone.name }),
            )
        },

        async getRoles(page = 1) {
            let roles = await this.roleModel.paginate(50, page).catch(() => {})

            if (!roles) return

            let data = roles.data

            if (roles.meta.pagination.total_pages > page) {
                data.concat(await this.getRoles(page + 1))
            }

            return data
        },

        async init() {
            if (!this.isDialog) {
                let roles = await this.getRoles()

                this.roles = roles
            }
        },

        async changeEmail() {
            this.showChangeEmailModal = true
        },

        async sendConfirmationEmail() {
            // Validate new email
            if (!email(this.model.newEmail)) {
                this.changeEmailError = this.$t(
                    'users-form.messages.email-error-email',
                )
                return
            }
            // Make the request
            await axios.post('users/changeEmail', {
                new_email: this.model.newEmail,
                user_id: this.model.id,
            })

            this.changeEmailError = ''
            this.showChangeEmailModal = false
            this.$bus.$emit(
                'notify',
                'success',
                this.$t('users-form.bus.confirmation-email-sent'),
            )
        },
    },

    mounted() {
        this.init()
    },

    computed: {
        nameErrors() {
            const errors = []
            if (!this.$v.model.name.$dirty) return errors

            !this.$v.model.name.maxLength &&
                errors.push(
                    this.$t('users-form.messages.name-error-max-length'),
                )

            !this.$v.model.name.required &&
                errors.push(this.$t('users-form.messages.name-error-required'))
            return errors
        },

        emailErrors() {
            const errors = []
            if (!this.$v.model.email.$dirty) return errors

            !this.$v.model.email.required &&
                errors.push(this.$t('users-form.messages.email-error-required'))

            !this.$v.model.email.email &&
                errors.push(this.$t('users-form.messages.email-error-email'))

            return errors
        },

        passwordErrors() {
            const errors = []
            if (!this.$v.model.password.$dirty) return errors

            if (!this.isEdit) {
                !this.$v.model.password.required &&
                    errors.push(
                        this.$t('users-form.messages.password-error-required'),
                    )
            }

            !this.$v.model.password.minLength &&
                errors.push(
                    this.$t('users-form.messages.password-error-min-length'),
                )

            return errors
        },

        passwordConfirmationErrors() {
            const errors = []
            if (!this.$v.model.password_confirmation.$dirty) return errors

            !this.$v.model.password_confirmation.sameAs &&
                errors.push(
                    this.$t('users-form.messages.password-error-confirmation'),
                )
            return errors
        },

        roleModel() {
            return new Role()
        },
    },
}
</script>
